/**
 * New prospect (CRM #6, 2026-09-28) — a company we are working that QX does not know yet.
 *
 *   openProspectSheet({ entity, prefill, from: { source, ref } })
 *
 * Callum: "both, one list". QX's own `#` accounts are NOT made here — they are read from the
 * mirror; this is for the company that has no QX account at all (a show lead, a web enquiry,
 * an emailed request, or somebody a rep met). Saving makes the prospect and a "set it up in
 * QX" task for its owner; a person links it to its QX code once that exists.
 *
 * `from` = the lead / web enquiry / request it came from: the server copies what it knows
 * and links the two, so the inbox shows "Prospect" on that row from then on.
 * The owner defaults to ME. Choosing somebody else uses the person picker, which only a
 * manager can search (`/team/users`); anybody else is told so rather than shown a dead box.
 */
import { escapeHtml as esc, getIcon, router, showModal, showSuccess, store } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { failed, newId, personPicker, saving } from './log-sheet.js';

const FIELDS = [
  ['company_name', 'Company', 'text', 200, true],
  ['contact_name', 'Contact', 'text', 200],
  ['contact_email', 'Their email', 'email', 255],
  ['contact_phone', 'Their phone', 'tel', 60],
  ['domain', 'Website / domain', 'text', 200],
  ['country', 'Country', 'text', 100],
];

export function openProspectSheet({ entity, prefill = {}, from = null, entities = null } = {}) {
  const id = newId();
  const me = store.get('user') || {};
  let owner = null;                        // null = me
  const content = document.createElement('div');
  const ents = (entities || []).length > 1 ? entities : null;
  content.innerHTML = `
    <p class="sc-sheet-hint">${getIcon('info', 12)} A company QX does not know yet. Saving gives its owner a task to
      set it up in QX; once it has a QX code, link it and everything logged here moves onto the account.</p>
    ${ents ? `<div class="form-group"><label class="form-label" for="sc-pr-entity">Entity</label>
      <select class="form-select" id="sc-pr-entity" style="width:auto">${ents.map(e =>
        `<option value="${esc(e)}"${e === entity ? ' selected' : ''}>${esc(e.toUpperCase())}</option>`).join('')}</select></div>` : ''}
    ${FIELDS.map(([k, label, type, max, req]) => `<div class="form-group">
      <label class="form-label" for="sc-pr-${k}">${esc(label)}${req ? '' : ' <span style="color:var(--text-tertiary);font-weight:400">(optional)</span>'}</label>
      <input class="form-input" id="sc-pr-${k}" type="${type}" maxlength="${max}" value="${esc(prefill[k] || '')}"></div>`).join('')}
    <div class="form-group"><label class="form-label" for="sc-pr-note">Note
      <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <textarea class="form-textarea" id="sc-pr-note" rows="3" maxlength="20000"
        placeholder="What they do, what they need, how we met them">${esc(prefill.note || '')}</textarea></div>
    <div class="form-group"><label class="form-label">Owner</label>
      <div data-owner-me class="sc-picked">${getIcon('user-check', 16)} <strong>${esc(me.name || me.display_name || 'You')}</strong>
        <button type="button" class="btn btn-ghost btn-sm" data-owner-change>Give it to somebody else</button></div>
      <div data-person hidden></div></div>
    ${from ? `<p class="sc-list-meta">From ${esc(from.source === 'lead' ? 'a show lead' : from.source === 'web' ? 'a web enquiry' : 'a quote request')}
      — it stays linked to it.</p>` : ''}`;

  const modal = showModal({
    title: 'New prospect', content, size: 'md',
    actions: [
      { label: 'Cancel', className: 'btn btn-secondary' },
      { label: 'Save', className: 'btn btn-primary', onClick: (close) => save(close) },
    ],
  });

  content.querySelector('[data-owner-change]').addEventListener('click', () => {
    content.querySelector('[data-owner-me]').hidden = true;
    const box = content.querySelector('[data-person]');
    box.hidden = false;
    personPicker(content, null, (p) => { owner = p; });
  });
  setTimeout(() => content.querySelector('#sc-pr-company_name').focus(), 80);

  let busy = false;
  async function save(close) {
    if (busy) return;
    const val = (k) => content.querySelector(`#sc-pr-${k}`).value.trim();
    const company = val('company_name');
    if (!company) { content.querySelector('#sc-pr-company_name').focus(); return; }
    const ent = ents ? content.querySelector('#sc-pr-entity').value : entity;
    const body = { id, entity: ent };
    FIELDS.forEach(([k]) => { const v = val(k); if (v) body[k] = v; });
    const note = content.querySelector('#sc-pr-note').value.trim();
    if (note) body.note = note;
    if (owner && owner.id) body.owner_user_id = owner.id;
    busy = true;
    saving(modal, true, 'Save');
    try {
      const res = from
        ? await salesApi.prospectFrom(from.source, from.ref, body)
        : await salesApi.createProspect(body);
      const saved = res.data || {};
      close();
      showSuccess(res.replayed ? 'Already saved.' : 'Prospect saved — a task to set it up in QX is on its owner\'s list.');
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: { entity: ent } }));
      router.navigate(`/prospects/${encodeURIComponent(saved.id || id)}`);
    } catch (err) {
      failed(err, 'The prospect');
      saving(modal, false, 'Save');
      busy = false;
    }
  }
}
